import spacetime from 'spacetime';
import {
  IAvailabilityOp,
  IAvailabilitySnapshot,
} from '@/interfaces/ICatalogV2';
import { ICategory } from '@/interfaces/ICategory';
import { IItem } from '@/interfaces/IItem';
import { ISimpleHourRange } from '@/interfaces/ISimpleHourRange';
import { hiddenInBranch } from './catalog-filters';

/**
 * Availability of products and categories on the Menú page (plan §5.9):
 * what the row switch does, the chooser it opens in multi-branch stores, the
 * undo of a change (the client mirror of the API's `invertAvailabilityOp`)
 * and the status line («Oculto», «Fuera de horario · vuelve…»).
 */

type TAvailabilityEntity = Pick<
  IItem | ICategory,
  'visible' | 'hiddenBranchesIds'
>;

/** Switch on = can be ordered and sold here: visible and not hidden in this branch. */
export function isAvailableHere(
  entity: TAvailabilityEntity,
  branchId: number | null
): boolean {
  return entity.visible && !hiddenInBranch(entity, branchId);
}

export interface IAvailabilityContext {
  /** The admin's current branch. */
  branchId: number | null;
  /** More than one active branch: turning off asks where. */
  multiBranch: boolean;
  /** Every branch of the store, inactive ones included. */
  allBranchIds: number[];
  /** The role manages every branch (`canHideEverywhere`). */
  canHideEverywhere: boolean;
}

export type TAvailabilityScope = 'here' | 'everywhere';

export interface IAvailabilityChoice {
  scope: TAvailabilityScope;
  op: IAvailabilityOp;
}

/**
 * What toggling the switch does: one tap (`apply`), a scope chooser
 * (`choose`), or nothing because the role cannot show it (`blocked`).
 */
export type TAvailabilityAction =
  | { kind: 'apply'; op: IAvailabilityOp }
  | { kind: 'choose'; turnOn: boolean; choices: IAvailabilityChoice[] }
  | { kind: 'blocked' };

export function toggleAvailability(
  entity: TAvailabilityEntity,
  turnOn: boolean,
  ctx: IAvailabilityContext
): TAvailabilityAction {
  const branchId = ctx.branchId;

  if (!turnOn) {
    // Roles limited to some branches never change `visible` (the API
    // refuses it): they hide in their branch.
    if (!ctx.canHideEverywhere) {
      if (branchId === null) return { kind: 'blocked' };
      const here: IAvailabilityChoice = {
        scope: 'here',
        op: { hideInBranchIds: [branchId] },
      };
      return ctx.multiBranch
        ? { kind: 'choose', turnOn: false, choices: [here] }
        : { kind: 'apply', op: here.op };
    }
    if (!ctx.multiBranch || branchId === null)
      return { kind: 'apply', op: { visible: false } };
    return {
      kind: 'choose',
      turnOn: false,
      choices: [
        { scope: 'here', op: { hideInBranchIds: [branchId] } },
        { scope: 'everywhere', op: { visible: false } },
      ],
    };
  }

  // Hidden in every branch: showing it is a chain-wide choice.
  if (!entity.visible && !ctx.canHideEverywhere) return { kind: 'blocked' };
  if (!entity.visible && ctx.multiBranch && branchId !== null) {
    return {
      kind: 'choose',
      turnOn: true,
      choices: [
        {
          scope: 'here',
          op: {
            visible: true,
            // Inactive branches included: reactivating one later must not show it there.
            hiddenBranchIds: ctx.allBranchIds.filter((id) => id !== branchId),
          },
        },
        { scope: 'everywhere', op: { visible: true, hiddenBranchIds: null } },
      ],
    };
  }

  // One tap clears whatever blocks selling here.
  const op: IAvailabilityOp = {};
  if (!entity.visible) op.visible = true;
  if (branchId !== null && hiddenInBranch(entity, branchId))
    op.showInBranchIds = [branchId];
  return { kind: 'apply', op };
}

interface IAvailabilityState {
  visible: boolean;
  /** Branch ids as stored (strings); `null` = hidden nowhere. */
  hiddenBranchesIds: string[] | null;
}

function toBranchIdStrings(ids: Array<number | string>): string[] | null {
  const unique = Array.from(
    new Set(ids.map((id) => String(id).trim()).filter((id) => id !== ''))
  );
  return unique.length ? unique : null;
}

/** Mirror of the API's `applyAvailabilityOp`. */
export function applyAvailabilityOp(
  current: IAvailabilityState,
  op: IAvailabilityOp
): IAvailabilityState {
  const visible = op.visible ?? current.visible;
  if (op.hiddenBranchIds !== undefined)
    return {
      visible,
      hiddenBranchesIds: toBranchIdStrings(op.hiddenBranchIds ?? []),
    };

  const show = new Set((op.showInBranchIds ?? []).map((id) => String(id)));
  return {
    visible,
    hiddenBranchesIds: toBranchIdStrings([
      ...(current.hiddenBranchesIds ?? []).filter((id) => !show.has(id.trim())),
      ...(op.hideInBranchIds ?? []),
    ]),
  };
}

/**
 * The undo of `op` applied to `before` (mirror of the API's
 * `invertAvailabilityOp`): deltas only, so undoing never overwrites branches
 * another device changed meanwhile.
 */
export function invertAvailabilityOp(
  before: IAvailabilitySnapshot,
  op: IAvailabilityOp
): IAvailabilityOp {
  const beforeState: IAvailabilityState = {
    visible: before.visible,
    hiddenBranchesIds: toBranchIdStrings(before.hiddenBranchIds ?? []),
  };
  const after = applyAvailabilityOp(beforeState, op);
  const beforeHidden = new Set(beforeState.hiddenBranchesIds ?? []);
  const afterHidden = new Set(after.hiddenBranchesIds ?? []);

  const inverse: IAvailabilityOp = {};
  if (after.visible !== before.visible) inverse.visible = before.visible;
  const showIn = [...afterHidden]
    .filter((id) => !beforeHidden.has(id))
    .map(Number);
  const hideIn = [...beforeHidden]
    .filter((id) => !afterHidden.has(id))
    .map(Number);
  if (showIn.length) inverse.showInBranchIds = showIn;
  if (hideIn.length) inverse.hideInBranchIds = hideIn;
  return inverse;
}

/** The snapshot an undo starts from, taken from a store row. */
export function availabilitySnapshot(
  entity: TAvailabilityEntity
): IAvailabilitySnapshot {
  return {
    visible: entity.visible,
    hiddenBranchIds:
      toBranchIdStrings(entity.hiddenBranchesIds ?? [])?.map(Number) ?? null,
  };
}

/** True when an op changes nothing (the toggle already matched). */
export function isEmptyAvailabilityOp(op: IAvailabilityOp): boolean {
  return (
    op.visible === undefined &&
    op.hiddenBranchIds === undefined &&
    !op.hideInBranchIds?.length &&
    !op.showInBranchIds?.length
  );
}

//#region Schedules

/** Schedules the storefront applies: days 0 (Sunday) to 6; day 7 is legacy «Festivos». */
function rangesOfDay(
  hours: readonly ISimpleHourRange[],
  day: number
): ISimpleHourRange[] {
  return hours
    .filter((range) => range.day === day)
    .sort((a, b) => a.from.localeCompare(b.from));
}

function clock(time: string): string {
  return time.slice(0, 5);
}

interface IZonedNow {
  day: number;
  time: string;
}

function zonedNow(tz: string, now: Date): IZonedNow {
  const moment = spacetime(now.getTime(), tz);
  const pad = (value: number) => String(value).padStart(2, '0');
  return {
    day: moment.day(),
    time: `${pad(moment.hour())}:${pad(moment.minute())}`,
  };
}

/** Mirror of the storefront's `timeInsideHours`, comparing on `HH:MM`. */
export function isInsideHours(
  hours: readonly ISimpleHourRange[],
  tz: string,
  now: Date
): boolean {
  const { day, time } = zonedNow(tz, now);
  return rangesOfDay(hours, day).some(
    (range) => time >= clock(range.from) && time <= clock(range.to)
  );
}

export interface INextOpening {
  /** 0 = later today, 1 = tomorrow… */
  dayOffset: number;
  /** 0 = Sunday. */
  weekday: number;
  /** `HH:MM`. */
  time: string;
}

/** When a closed schedule opens next, within a week; null when never (only day-7 rows). */
export function nextOpening(
  hours: readonly ISimpleHourRange[],
  tz: string,
  now: Date
): INextOpening | null {
  const { day, time } = zonedNow(tz, now);
  for (let offset = 0; offset <= 7; offset++) {
    const weekday = (day + offset) % 7;
    const next = rangesOfDay(hours, weekday).find(
      (range) => offset > 0 || clock(range.from) > time
    );
    if (next) return { dayOffset: offset, weekday, time: clock(next.from) };
  }
  return null;
}

export interface IScheduleContext {
  /** The store's effective plan tier: schedules apply from tier 1. */
  tier: number;
  /** The branch's time zone; schedules apply only with one. */
  tz: string | null;
  now: Date;
}

export type TScheduleStatus =
  | { key: 'scheduleInactive' }
  | { key: 'outOfHours'; next: INextOpening | null };

function laterOpening(a: INextOpening, b: INextOpening): INextOpening {
  return a.dayOffset * 1440 + minutes(a.time) >=
    b.dayOffset * 1440 + minutes(b.time)
    ? a
    : b;
}

function minutes(time: string): number {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
}

/**
 * The time-dependent part of a row's status. Every schedule that applies
 * blocks on its own (the product's and its category's), so the row reopens
 * at the later of their openings.
 */
export function scheduleStatus(
  schedules: ReadonlyArray<readonly ISimpleHourRange[] | null | undefined>,
  ctx: IScheduleContext
): TScheduleStatus | null {
  const active = schedules.filter(
    (hours): hours is readonly ISimpleHourRange[] => !!hours?.length
  );
  if (!active.length) return null;
  if (ctx.tier < 1) return { key: 'scheduleInactive' };
  if (!ctx.tz) return null;

  const tz = ctx.tz;
  const blocking = active.filter((hours) => !isInsideHours(hours, tz, ctx.now));
  if (!blocking.length) return null;
  const openings = blocking.map((hours) => nextOpening(hours, tz, ctx.now));
  const [first, ...rest] = openings;
  // One schedule that never opens (only day-7 rows) keeps the row closed.
  const next =
    first && rest.every((opening): opening is INextOpening => !!opening)
      ? rest.reduce(laterOpening, first)
      : null;
  return { key: 'outOfHours', next };
}

//#endregion

export type TAvailabilityStatus = { key: 'hidden' } | { key: 'hiddenHere' };

/** The static part of a row's status: hidden everywhere, or here. */
export function availabilityStatus(
  entity: TAvailabilityEntity,
  branchId: number | null
): TAvailabilityStatus | null {
  if (!entity.visible) return { key: 'hidden' };
  if (hiddenInBranch(entity, branchId)) return { key: 'hiddenHere' };
  return null;
}
